/**
 * Joining a scope repository another device already filled (card 01fee74a):
 * when THIS device's scope is empty — no history in its folder and no data
 * files (only Apiboo's own manifest and git setup files) — Connect / Join
 * adopts the remote branch as it is, like `git clone`: no starting commit of
 * its own, no merge, one root. What the remote holds is then applied to the
 * app the same way a pull applies it (before = nothing, after = its files).
 *
 * If the app cannot apply all of it, the adopt is undone (`git_undo_adopt`:
 * the branch goes back to unborn — it only had the remote's commits) and the
 * result is 'not_applied', exactly like a connect whose merge could not be
 * applied: left adopted, the next refresh would write the app's (empty)
 * state over the files and the next sync would push that as a deletion.
 *
 * Anything else — local data, a history in the folder, an empty remote, an
 * unreachable one — returns null and the connect goes the ordinary way.
 */
import { invoke } from '@tauri-apps/api/core';
import { applyScopeChanges } from './scopeRepo';
import { isScopeManaged } from './scopeRepoLayout';
import { listAllFiles } from '../gitFileWriter';

/** No item of the scope on disk: no document, board, task, project or media file (the manifest and git setup files do not count). */
async function holdsNoItems(dir) {
  const fs = await import('@tauri-apps/plugin-fs');
  return (await listAllFiles(fs, dir, isScopeManaged)).length === 0;
}

const problemsOf = (applied) => Object.values(applied || {}).flatMap((r) => r?.problems || []);

/**
 * @param {{ key: string }} scope
 * @param {string} dir the scope folder (git_init_or_open + origin already set)
 * @param {{ cred: object, branch: string, readDataFiles: (dir: string) => Promise<Record<string,string>> }} opts
 * @returns {Promise<null | { status: 'adopted'|'not_applied', applied: object, problems?: Array }>}
 */
export async function adoptIfEmpty(scope, dir, { cred, branch, readDataFiles }) {
  if (!(await holdsNoItems(dir))) return null; // local content is real: commit + merge as before
  const statusArgs = { repoPath: dir, remote: 'origin', branch };
  if ((await invoke('git_status', statusArgs).catch(() => null))?.headOid) return null; // a history here: never dropped
  try {
    await invoke('git_fetch', { ...statusArgs, ...cred });
  } catch {
    return null; // the ordinary path reports what is wrong with the remote
  }
  if (!(await invoke('git_status', statusArgs).catch(() => null))?.remoteOid) return null; // an empty repository: the first push fills it

  // The caller's decision: the local side is empty — nothing of it is kept.
  await invoke('git_adopt_remote', { ...statusArgs, discardLocal: true });
  const applied = await applyScopeChanges(scope.key, { before: {}, after: await readDataFiles(dir), dir });
  const problems = problemsOf(applied);
  if (problems.length) {
    await invoke('git_undo_adopt', statusArgs);
    return { status: 'not_applied', applied, problems };
  }
  return { status: 'adopted', applied };
}
